/**
 * cart-drawer.js — <cart-drawer>
 *
 * Every mutation follows the same path: POST to the Cart AJAX API, ask Shopify
 * to re-render the cart-drawer section in the same response, and swap the
 * markup in. Cart state is never reconstructed client-side, so the drawer
 * cannot disagree with the real cart — the failure mode that makes a store
 * feel untrustworthy at exactly the wrong moment.
 *
 * Failures surface. A request that errors restores the previous quantity and
 * says what happened, rather than leaving a control in a state the server
 * never agreed to.
 */

import { announce } from './theme.js';

const SECTION_ID = 'cart-drawer';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));

    // Any part of the theme can ask the cart to refresh and open — the product
    // form in Phase 3 uses this rather than reaching into the drawer.
    document.addEventListener('theme:cart:refresh', (event) => {
      this.refresh({ open: event.detail?.open !== false });
    });
  }

  get overlay() {
    return this.querySelector('theme-overlay');
  }

  onClick(event) {
    const step = event.target.closest('[data-quantity-step]');
    if (step) {
      event.preventDefault();
      const line = step.closest('[data-cart-line]');
      const input = line?.querySelector('[data-quantity-input]');
      if (!input) return;
      const next = Math.max(0, parseInt(input.value, 10) + parseInt(step.dataset.quantityStep, 10));
      this.change(line, next);
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      const line = remove.closest('[data-cart-line]');
      if (line) this.change(line, 0);
    }
  }

  onChange(event) {
    const input = event.target.closest('[data-quantity-input]');
    if (!input) return;

    const line = input.closest('[data-cart-line]');
    const quantity = Math.max(0, parseInt(input.value, 10) || 0);
    this.change(line, quantity);
  }

  async change(line, quantity) {
    if (!line) return;

    const key = line.dataset.lineKey;
    const previous = line.querySelector('[data-quantity-input]')?.value;
    line.classList.add('is-busy');

    try {
      const response = await fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: SECTION_ID }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Cart update failed');
      }

      this.render(data.sections?.[SECTION_ID]);
      this.updateCount(data.item_count);
      announce(window.themeStrings?.cartUpdated || 'Cart updated');
    } catch (error) {
      line.classList.remove('is-busy');
      const input = line.querySelector('[data-quantity-input]');
      if (input && previous !== undefined) input.value = previous;
      this.showError(line, error.message);
    }
  }

  async refresh({ open = false } = {}) {
    try {
      const response = await fetch(`${this.root}?section_id=${SECTION_ID}`);
      if (!response.ok) throw new Error('Cart refresh failed');
      this.render(await response.text());
      if (open) this.overlay?.open();
    } catch (error) {
      // A failed refresh must not strand the customer: send them to the cart
      // page, which always renders server-side.
      window.location.href = `${this.root}cart`;
    }
  }

  /**
   * Replaces the drawer's contents while preserving whether it was open, so a
   * quantity change never closes the drawer underneath the customer's finger.
   */
  render(markup) {
    if (!markup) return;

    const wasOpen = this.overlay?.classList.contains('is-open');
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const next = parsed.querySelector('cart-drawer');
    if (!next) return;

    this.innerHTML = next.innerHTML;

    if (wasOpen) {
      const overlay = this.overlay;
      overlay.hidden = false;
      overlay.classList.add('is-open');
    }
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = count > 0 ? `(${count})` : '';
    });
  }

  showError(line, message) {
    let notice = line.querySelector('[data-cart-error]');
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'notice notice--critical type-meta';
      notice.setAttribute('role', 'alert');
      notice.dataset.cartError = '';
      line.querySelector('.cart-line__detail')?.appendChild(notice);
    }
    notice.textContent = message;
    announce(message);
  }

  get root() {
    return window.Shopify?.routes?.root || '/';
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

export default CartDrawer;
